import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, Calculator, AlertTriangle, CheckCircle, ArrowRight,
  Clock, DollarSign, ChevronDown, ChevronUp, Zap, Star, XCircle
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const LimiteFaturamentoMeiPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [faturamentoMensal, setFaturamentoMensal] = useState('');
  const [mesesAtivos, setMesesAtivos] = useState(12);
  const [resultado, setResultado] = useState(null);

  const navigateTo = (url) => {
    window.location.href = url;
  };

  const calcularLimite = () => {
    const mensal = parseFloat(faturamentoMensal.replace(/\D/g, '')) / 100 || 0;
    const anual = mensal * 12;
    const limiteAnual = 81000;
    const limiteProporcional = (81000 / 12) * mesesAtivos;
    
    let status = 'ok';
    let mensagem = '';
    let detalhe = '';

    if (mesesAtivos < 12) {
      if (anual > limiteProporcional * 1.2) {
        status = 'excedeu';
        mensagem = 'Limite proporcional excedido em mais de 20%!';
        detalhe = `Seu limite proporcional é R$ ${limiteProporcional.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}. Você precisa migrar para ME.`;
      } else if (anual > limiteProporcional) {
        status = 'atencao';
        mensagem = 'Limite proporcional excedido até 20%';
        detalhe = `Pagará guia complementar mas pode continuar como MEI.`;
      } else {
        status = 'ok';
        mensagem = 'Tudo certo! Dentro do limite proporcional.';
        detalhe = `Pode faturar mais R$ ${(limiteProporcional - anual).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} este ano.`;
      }
    } else {
      if (anual > limiteAnual * 1.2) {
        status = 'excedeu';
        mensagem = 'Limite anual excedido em mais de 20%!';
        detalhe = `Você precisa migrar para ME retroativamente.`;
      } else if (anual > limiteAnual) {
        status = 'atencao';
        mensagem = 'Limite anual excedido até 20%';
        detalhe = `Pagará guia complementar sobre o excedente.`;
      } else if (anual > limiteAnual * 0.8) {
        status = 'alerta';
        mensagem = 'Atenção! Próximo do limite.';
        detalhe = `Faltam R$ ${(limiteAnual - anual).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} para o limite.`;
      } else {
        status = 'ok';
        mensagem = 'Tudo certo! Dentro do limite.';
        detalhe = `Pode faturar mais R$ ${(limiteAnual - anual).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} este ano.`;
      }
    }

    setResultado({ status, mensagem, detalhe, anual, limite: mesesAtivos < 12 ? limiteProporcional : limiteAnual });
  };

  const faqs = [
    {
      pergunta: 'Qual o limite de faturamento do MEI em 2025?',
      resposta: 'O limite é de R$ 81.000,00 por ano (média de R$ 6.750,00/mês). Esse valor não foi alterado em relação a 2024.'
    },
    {
      pergunta: 'O que acontece se ultrapassar o limite?',
      resposta: 'Até 20% (R$ 97.200): paga guia complementar e continua MEI. Mais de 20%: migra para ME retroativamente.'
    },
    {
      pergunta: 'Abri o MEI no meio do ano. Qual meu limite?',
      resposta: 'O limite é proporcional. Ex: abriu em julho (6 meses) = R$ 40.500 (R$ 81.000 ÷ 12 × 6).'
    },
    {
      pergunta: 'Como é calculado o faturamento?',
      resposta: 'É a receita bruta total (com ou sem nota fiscal). Não desconta custos ou despesas.'
    },
    {
      pergunta: 'O limite vai aumentar para R$ 130.000?',
      resposta: 'Existe projeto de lei, mas ainda não foi aprovado. Em 2025, continua R$ 81.000.'
    },
  ];

  const cenarios = [
    {
      titulo: 'Dentro do Limite',
      faturamento: 'Até R$ 81.000',
      icone: CheckCircle,
      cor: 'emerald',
      consequencia: 'Continue normalmente',
      acao: 'Nenhuma ação necessária'
    },
    {
      titulo: 'Excedeu até 20%',
      faturamento: 'R$ 81.001 a R$ 97.200',
      icone: AlertTriangle,
      cor: 'amber',
      consequencia: 'Paga guia complementar',
      acao: 'Continua MEI no próximo ano'
    },
    {
      titulo: 'Excedeu mais de 20%',
      faturamento: 'Acima de R$ 97.200',
      icone: XCircle,
      cor: 'red',
      consequencia: 'Migração para ME',
      acao: 'Efeito retroativo'
    },
  ];

  return (
    <div className="min-h-screen font-sans bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Limite de Faturamento MEI 2025: Guia Completo",
          "author": { "@type": "Organization", "name": "MeuMei" }
        })
      }} />

      <Header />

      {/* Hero */}
      <section 
        className="relative pt-20 pb-16 md:pt-32 md:pb-24 px-4 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
      >
        <div className="max-w-4xl mx-auto relative z-10">
          <nav className="text-white/70 text-sm mb-6">
            <a href="/" className="hover:text-white">Início</a>
            <span className="mx-2">›</span>
            <a href="/blog" className="hover:text-white">Blog</a>
            <span className="mx-2">›</span>
            <span className="text-white">Limite Faturamento MEI</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              <TrendingUp className="w-4 h-4" />
              Atualizado 2025
            </motion.span>

            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              Limite de Faturamento<br />
              <span className="text-yellow-200">MEI 2025</span>
            </h1>
            
            <p className="text-lg md:text-xl max-w-2xl mx-auto opacity-90 mb-8">
              Entenda o limite de R$ 81.000, como calcular proporcional e o que fazer se ultrapassar.
            </p>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* CTA Mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg z-40 md:hidden">
        <motion.button
          onClick={() => navigateTo('https://app.meumei.net/register')}
          whileTap={{ scale: 0.98 }}
          className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-xl"
        >
          Controlar Faturamento com MeuMei
        </motion.button>
      </div>

      {/* Conteúdo */}
      <article className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Destaque do limite */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-8 text-center"
            >
              <p className="text-amber-700 font-medium mb-2">Limite de Faturamento MEI 2025</p>
              <p className="text-5xl md:text-6xl font-bold text-amber-600 mb-2">R$ 81.000</p>
              <p className="text-amber-700">por ano (média de R$ 6.750/mês)</p>
            </motion.div>
          </section>

          {/* Calculadora */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              🧮 Calculadora: Você está dentro do limite?
            </h2>
            
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Faturamento médio mensal
                  </label>
                  <input
                    type="text"
                    value={faturamentoMensal}
                    onChange={(e) => {
                      const valor = e.target.value.replace(/\D/g, '');
                      const formatado = (parseInt(valor || 0) / 100).toLocaleString('pt-BR', {
                        style: 'currency', currency: 'BRL'
                      });
                      setFaturamentoMensal(formatado);
                    }}
                    placeholder="R$ 0,00"
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meses como MEI este ano
                  </label>
                  <select
                    value={mesesAtivos}
                    onChange={(e) => setMesesAtivos(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-amber-500"
                  >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <option key={m} value={m}>{m} {m === 1 ? 'mês' : 'meses'}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={calcularLimite}
                className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors"
              >
                Calcular
              </button>

              {resultado && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-6 p-4 rounded-xl ${
                    resultado.status === 'ok' ? 'bg-green-100 border border-green-300' :
                    resultado.status === 'alerta' ? 'bg-yellow-100 border border-yellow-300' :
                    resultado.status === 'atencao' ? 'bg-amber-100 border border-amber-300' :
                    'bg-red-100 border border-red-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {resultado.status === 'ok' ? <CheckCircle className="w-6 h-6 text-green-600" /> :
                     resultado.status === 'alerta' ? <AlertTriangle className="w-6 h-6 text-yellow-600" /> :
                     resultado.status === 'atencao' ? <AlertTriangle className="w-6 h-6 text-amber-600" /> :
                     <XCircle className="w-6 h-6 text-red-600" />}
                    <div>
                      <p className="font-bold">{resultado.mensagem}</p>
                      <p className="text-sm mt-1">{resultado.detalhe}</p>
                      <p className="text-xs mt-2 opacity-75">
                        Projeção anual: R$ {resultado.anual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | 
                        Limite: R$ {resultado.limite.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </section>

          {/* Cenários */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              📊 O que acontece em cada cenário
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              {cenarios.map((cenario, index) => (
                <motion.div
                  key={cenario.titulo}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-6 rounded-2xl border-2 ${
                    cenario.cor === 'emerald' ? 'bg-emerald-50 border-emerald-200' :
                    cenario.cor === 'amber' ? 'bg-amber-50 border-amber-200' :
                    'bg-red-50 border-red-200'
                  }`}
                >
                  <cenario.icone className={`w-10 h-10 mb-4 ${
                    cenario.cor === 'emerald' ? 'text-emerald-500' :
                    cenario.cor === 'amber' ? 'text-amber-500' :
                    'text-red-500'
                  }`} />
                  <h3 className="font-bold text-gray-800 mb-1">{cenario.titulo}</h3>
                  <p className="text-sm text-gray-600 mb-3">{cenario.faturamento}</p>
                  <p className="text-sm font-medium text-gray-700">{cenario.consequencia}</p>
                  <p className="text-xs text-gray-500 mt-1">{cenario.acao}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              ❓ Perguntas Frequentes
            </h2>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="border rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50"
                  >
                    <span className="font-semibold text-gray-800 pr-4">{faq.pergunta}</span>
                    {openFaq === index ? <ChevronUp className="w-5 h-5 text-amber-600" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-gray-600">{faq.resposta}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Final */}
          <section>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-3xl p-8 md:p-12 text-center text-white"
            >
              <h2 className="text-2xl md:text-4xl font-bold mb-4">
                Controle seu faturamento automaticamente
              </h2>
              <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
                Com o MeuMei você acompanha seu faturamento em tempo real e recebe alertas antes de atingir o limite!
              </p>
              <motion.button
                onClick={() => navigateTo('https://app.meumei.net/register')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white text-amber-600 font-bold rounded-xl shadow-lg"
              >
                Começar Grátis - 7 Dias Free
              </motion.button>
            </motion.div>
          </section>

        </div>
      </article>

      <div className="h-24 md:hidden" />
      <Footer />
    </div>
  );
};

export default LimiteFaturamentoMeiPage;